"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import dynamic from "next/dynamic"
import { Smartphone, MailboxIcon } from "lucide-react"

import { Logo } from "@/components/molecules/Logo"
import useLocaleStore from "@/app/stores/useLocaleStore"
import { getMessages } from "@/app/utils/getTranslation"
import LoadingGear from "@/components/molecules/Loader"
import { Tiktok } from "@/components/atoms/TiktokIcon"

const Instagram = dynamic(() => import("lucide-react").then(mod => mod.Instagram), { ssr: false })
const Facebook = dynamic(() => import("lucide-react").then(mod => mod.Facebook), { ssr: false })
const Linkedin = dynamic(() => import("lucide-react").then(mod => mod.Linkedin), { ssr: false })

type LinkData = {
  title: string
  link: string
}

type FooterMessages = {
  footer: {
    description: string
    sections: {
      quickLinks: {
        title: string
        links: LinkData[]
      }
      importantLinks: {
        title: string
        links: LinkData[]
      }
      address: {
        title: string
        location: string
      }
    }
  }
}

const Footer = () => {
  const locale = useLocaleStore((state) => state.locale)
  const [messages, setMessages] = useState<FooterMessages | null>(null)

  useEffect(() => {
    const fetchMessages = async () => {
      const fetchedMessages = await getMessages(locale, "footer")
      setMessages(fetchedMessages)
    }
    fetchMessages()
  }, [locale])

  if (!messages) return <LoadingGear />

  const { footer } = messages

  return (
    <footer
      id="footer"
      aria-label="Site Footer"
      className="w-full bg-white dark:bg-gray-950 border-t border-gray-200 dark:border-gray-800"
    >
      <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-8 py-10 lg:grid-cols-3">
          {/* Company Info */}
          <div>
            <Logo />
            <p className="mt-4 text-gray-600 dark:text-white/70 lg:pr-20">
              {footer.description}
            </p>
          </div>

          {/* Footer Links */}
          <div className="col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-8">
            {[footer.sections.quickLinks, footer.sections.importantLinks].map(
              (section, i) => (
                <div key={i}>
                  <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                    {section.title}
                  </h2>
                  <ul className="space-y-2">
                    {section.links.map((link, index) => (
                      <li key={index}>
                        <Link
                          href={link.link}
                          className="text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors"
                        >
                          {link.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            )}

            {/* Company Address */}
            <div>
              <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                {footer.sections.address.title}
              </h2>
              <div className="space-y-4">
                <address className="not-italic">
                  <div className="flex place-items-center gap-3 text-gray-600 dark:text-gray-400">
                    <MailboxIcon className="h-6 w-6 shrink-0 text-primary align-middle" />
                    <p className="whitespace-pre-line">
                      {`Habesha Network
                      P.O. Box 32 
                      Lavon, TX 75166`}
                    </p>
                  </div>
                </address>
                <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
                  <Smartphone className="h-6 w-6 text-primary" />
                  <p>+1 (855) 553-7873</p>
                </div>
                <div className="flex items-center gap-4 pt-2">
                  <Link
                    href="https://www.instagram.com/yehabeshanetwork"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram Profile"
                  >
                    <Instagram className="h-6 w-6 hover:text-primary transition-colors" />
                  </Link>
                  <Link
                    href="https://web.facebook.com/profile.php?id=61580206130872&mibextid=wwXIfr&rdid=d3D8RRococHd8yrA&share_url=https%3A%2F%2Fweb.facebook.com%2Fshare%2F1A8A6RoiUN%2F%3Fmibextid%3DwwXIfr%26ref%3Dwaios.fb_links_xma_control%26_rdc%3D1%26_rdr"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook Profile"
                  >
                    <Facebook className="h-6 w-6 hover:text-primary transition-colors" />
                  </Link>
                  <Link
                    href="https://www.tiktok.com/@habeshanetwork?_r=1&_t=ZT-92AXXvMwbYt"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Tiktok Profile"
                  >
                    <Tiktok className="h-6 w-6 hover:text-primary transition-colors" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-200 dark:border-gray-800 py-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            © {new Date().getFullYear()} Habesha Network. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
